import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react"

export interface TicketPriceInfoBottomSheetHandle {
  openModal: () => void
  closeModal: () => void
}

const TicketPriceInfoBottomSheet = forwardRef<
  TicketPriceInfoBottomSheetHandle,
  { onClose: () => void }
>(({ onClose }, ref) => {
  const [isOpen, setIsOpen] = useState(false)

  useImperativeHandle(ref, () => ({
    openModal: () => setIsOpen(true),
    closeModal: () => setIsOpen(false),
  }))

  // Handle back button for ticket price info modal
  useEffect(() => {
    if (isOpen) {
      window.history.pushState(
        { ticketPriceInfoModalOpen: true },
        "",
        window.location.href,
      )
      const handlePopState = (event: PopStateEvent) => {
        if (!event.state?.ticketPriceInfoModalOpen) {
          event.stopImmediatePropagation() // Prevent other handlers
          setIsOpen(false)
          onClose()
        }
      }
      window.addEventListener("popstate", handlePopState, true) // Use capture phase
      return () => {
        window.removeEventListener("popstate", handlePopState, true)
      }
    }
  }, [isOpen, onClose])

  // Clean up history when modal closes normally (via X button)
  useEffect(() => {
    if (!isOpen && window.history.state?.ticketPriceInfoModalOpen) {
      // Replace current state to remove modal indicator instead of going back
      window.history.replaceState(
        { ...window.history.state, ticketPriceInfoModalOpen: false },
        "",
        window.location.href,
      )
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/20 -mb-10"
      onClick={onClose}
      style={{ pointerEvents: "auto" }}
    >
      <div
        className="w-full md:max-w-md bg-[#252525B2]/90 backdrop-blur-xl rounded-t-[32px] rounded-b-none p-8 pt-6 flex flex-col items-center shadow-2xl animate-slideUp relative  overflow-y-auto no-scrollbar h-[60%]"
        style={{
          borderTopLeftRadius: 32,
          borderTopRightRadius: 32,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col justify-between items-center w-full gap-6">
          <div className="text-7xl text-center w-full">🎟️</div>
          <div className="text-white font-medium text-lg">
            Set the price per ticket (taxes and fees excluded) and the total
            number of tickets you want to sell for this ticket type.
          </div>
          <div className="text-white/50 text-md italic">
            Example – to sell ten VIP tickets at $20 each: type 20 in Price and
            10 in Total tickets.
          </div>
          <div className="text-white/50 text-md italic">
            You’ll earn $200 after the sell of 10 VIP tickets
          </div>
        </div>
      </div>
    </div>
  )
})

export default TicketPriceInfoBottomSheet
